import React, { useEffect, useState } from "react";
import {
  FaCheck,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaMoneyBillWave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import CompactActionBar from "../components/CompactActionBar";
import ConfirmDialog from "../components/ConfirmDialog";
import "../css/standard-components.css";
import {
  HiddenIncomeAuth,
  hiddenIncomeAuthService,
} from "../services/hiddenIncomeAuthService";
import { formatCurrency } from "../utils/formatters";
import { projectService, Project } from "../services/projectService";

const HiddenIncomeAuthorize: React.FC = () => {
  const [incomes, setIncomes] = useState<HiddenIncomeAuth[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncomes, setSelectedIncomes] = useState<string[]>([]);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [incomeToReject, setIncomeToReject] = useState<HiddenIncomeAuth | null>(
    null
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState<HiddenIncomeAuth | null>(
    null
  );
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [approvalDate, setApprovalDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [isSingleApprove, setIsSingleApprove] = useState(false);
  const [singleApproveId, setSingleApproveId] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Filter toggle
  const [showFilters, setShowFilters] = useState(false);
  // Individual filter states
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterUnit, setFilterUnit] = useState("");
  const [filterBookingRef, setFilterBookingRef] = useState("");
  const [filterIncomeType, setFilterIncomeType] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterProject, setFilterProject] = useState("");
  // Applied snapshot for triggering reload
  const [appliedFilters, setAppliedFilters] = useState({
    customer: "",
    unit: "",
    bookingRef: "",
    incomeType: "",
    dateFrom: "",
    dateTo: "",
    project: "",
  });

  useEffect(() => {
    fetchProjects();
    fetchPendingIncomes();
  }, [currentPage, appliedFilters]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects(true);
      setProjects(data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const fetchPendingIncomes = async () => {
    try {
      setLoading(true);
      
      // Build filters object to send to API
      const filters: any = {};
      if (appliedFilters.customer) filters.customer = appliedFilters.customer;
      if (appliedFilters.unit) filters.unit = appliedFilters.unit;
      if (appliedFilters.bookingRef) filters.bookingRef = appliedFilters.bookingRef;
      if (appliedFilters.incomeType) filters.incomeType = appliedFilters.incomeType;
      if (appliedFilters.dateFrom) filters.dateFrom = appliedFilters.dateFrom;
      if (appliedFilters.dateTo) filters.dateTo = appliedFilters.dateTo;
      if (appliedFilters.project) {
        // Find project ID by name
        const matchedProject = projects.find(p => 
          p.name.toLowerCase().includes(appliedFilters.project.toLowerCase())
        );
        if (matchedProject) {
          filters.projectId = matchedProject.id;
        }
      }

      const response = await hiddenIncomeAuthService.getPendingHiddenIncomes(
        currentPage,
        limit,
        filters
      );

      if (response.emergency) {
        setEmergencyMode(true);
        setIncomes([]);
        setTotal(0);
        setTotalPages(1);
      } else {
        setEmergencyMode(false);
        // Data is already filtered by backend, no need for client-side filtering
        setIncomes(response.data);
        setTotal(response.pagination.total);
        setTotalPages(response.pagination.pages);
      }
    } catch (error) {
      console.error("Error fetching pending hidden incomes:", error);
      setIncomes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIncome = (incomeId: string) => {
    setSelectedIncomes((prev) =>
      prev.includes(incomeId)
        ? prev.filter((id) => id !== incomeId)
        : [...prev, incomeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIncomes.length === incomes.length) {
      setSelectedIncomes([]);
    } else {
      setSelectedIncomes(incomes.map((income) => income.id));
    }
  };

  const handleBulkApprove = () => {
    if (selectedIncomes.length > 0) {
      setIsSingleApprove(false);
      setSingleApproveId(null);
      setApprovalDate(new Date().toISOString().split('T')[0]);
      setShowApproveDialog(true);
    }
  };

  const confirmApprove = async () => {
    setShowApproveDialog(false);
    try {
      const idsToApprove = isSingleApprove && singleApproveId 
        ? [singleApproveId] 
        : selectedIncomes;
      
      await hiddenIncomeAuthService.approveHiddenIncomes(idsToApprove, approvalDate);
      setSelectedIncomes([]);
      setSingleApproveId(null);
      setIsSingleApprove(false);
      fetchPendingIncomes();
    } catch (error: any) {
      console.error("Error approving hidden incomes:", error);
      alert(error.response?.data?.message || "Failed to approve hidden incomes");
    }
  };

  const handleSingleApprove = (incomeId: string) => {
    setIsSingleApprove(true);
    setSingleApproveId(incomeId);
    setApprovalDate(new Date().toISOString().split('T')[0]);
    setShowApproveDialog(true);
  };

  const handleRejectClick = (income: HiddenIncomeAuth) => {
    setIncomeToReject(income);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (incomeToReject && rejectionReason.trim()) {
      setShowRejectDialog(false);
      try {
        await hiddenIncomeAuthService.rejectHiddenIncome(
          incomeToReject.id,
          rejectionReason
        );
        setSelectedIncomes([]);
        setIncomeToReject(null);
        setRejectionReason("");
        fetchPendingIncomes();
      } catch (error: any) {
        console.error("Error rejecting hidden income:", error);
        alert(error.response?.data?.message || "Failed to reject hidden income");
      }
    }
  };

  const confirmBulkReject = async () => {
    if (selectedIncomes.length > 0 && rejectionReason.trim()) {
      setShowBulkRejectDialog(false);
      try {
        // Reject each income individually since the API doesn't support bulk reject
        for (const incomeId of selectedIncomes) {
          await hiddenIncomeAuthService.rejectHiddenIncome(
            incomeId,
            rejectionReason
          );
        }
        setSelectedIncomes([]);
        setRejectionReason("");
        fetchPendingIncomes();
      } catch (error: any) {
        console.error("Error rejecting hidden incomes:", error);
        alert(error.response?.data?.message || "Failed to reject hidden incomes");
      }
    }
  };

  const handleViewDetails = (income: HiddenIncomeAuth) => {
    setSelectedIncome(income);
    setShowDetailsModal(true);
  };

  const applyFilters = () => {
    setAppliedFilters({
      customer: filterCustomer,
      unit: filterUnit,
      bookingRef: filterBookingRef,
      incomeType: filterIncomeType,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      project: filterProject,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilterCustomer("");
    setFilterUnit("");
    setFilterBookingRef("");
    setFilterIncomeType("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterProject("");
    setAppliedFilters({
      customer: "",
      unit: "",
      bookingRef: "",
      incomeType: "",
      dateFrom: "",
      dateTo: "",
      project: "",
    });
    setCurrentPage(1);
  };

  if (loading && incomes.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
        Loading pending receivable incomes...
      </div>
    );
  }

  return (
    <>
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <button
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSearch /> Filters
            </button>

            <div className="cab-filter-group">
              <span>Project:</span>
              <select
                className="cab-select"
                value={filterProject}
                onChange={(e) => {
                  setFilterProject(e.target.value);
                  setAppliedFilters((prev) => ({ ...prev, project: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.name}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        }
        rightActions={
          <>
            {selectedIncomes.length > 0 && !emergencyMode && (
              <button 
                className="btn btn-success"
                onClick={handleBulkApprove}
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                <FaCheck /> Approve ({selectedIncomes.length})
              </button>
            )}
          </>
        }
      />

      {/* Emergency Mode Alert */}
      {emergencyMode && (
        <div style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          color: "#dc2626",
          padding: "16px 20px",
          margin: "0",
          display: "flex",
          alignItems: "center",
          gap: "12px"
        }}>
          <FaExclamationTriangle style={{ fontSize: "20px" }} />
          <div>
            <strong>Emergency Mode Active</strong>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px" }}>
              Receivable income authorization is suspended during emergency mode. Receivables database is not available.
            </p>
          </div>
        </div>
      )}

      {/* Collapsible Filters */}
      {showFilters && !emergencyMode && (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyFilters(); } }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Customer</label>
              <input type="text" value={filterCustomer} onChange={e=>setFilterCustomer(e.target.value)} className="form-input" placeholder="Customer name" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Unit</label>
              <input type="text" value={filterUnit} onChange={e=>setFilterUnit(e.target.value)} className="form-input" placeholder="Unit ref" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Booking Ref</label>
              <input type="text" value={filterBookingRef} onChange={e=>setFilterBookingRef(e.target.value)} className="form-input" placeholder="Booking ref" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Income Type</label>
              <select value={filterIncomeType} onChange={e=>setFilterIncomeType(e.target.value)} className="form-input">
                <option value="">All</option>
                <option value="booking_amount">Booking Amount</option>
                <option value="agreement_amount">Agreement Amount</option>
                <option value="extra_work">Extra Work</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Date From</label>
              <input type="date" value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)} className="form-input" />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'12px', fontWeight:500, marginBottom:4 }}>Date To</label>
              <input type="date" value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)} className="form-input" />
            </div>
          </div>
          <div style={{ marginTop:16, display:'flex', gap:'8px' }}>
            <button onClick={applyFilters} className="btn btn-success" style={{ padding:'8px 16px', fontSize:'13px' }}>Apply</button>
            <button onClick={clearFilters} className="btn btn-secondary" style={{ padding:'8px 16px', fontSize:'13px' }}>Clear</button>
          </div>
        </div>
      )}

      {/* Table Section */}
      {!emergencyMode && (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          {incomes.length === 0 ? (
            <div className="no-data">
              <FaMoneyBillWave size={64} color="#9ca3af" />
              <h3>No Pending Receivable Income Authorizations</h3>
              <p>All receivable income records have been processed.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="standard-table">
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Project</th>
                      <th>Unit No</th>
                      <th>Customer Name</th>
                      <th>Income Type</th>
                      <th>Amount</th>
                      <th>Received Date</th>
                      <th>Request Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomes.map((income, index) => (
                      <tr key={income.id}>
                        <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                          {index + 1}
                        </td>
                        <td>
                          <span className="project-name">
                            {income.projectName || <span style={{ color: "#9ca3af" }}>N/A</span>}
                          </span>
                        </td>
                        <td>
                          <span className="unit-number">{income.unitRef}</span>
                        </td>
                        <td>
                          <span className="customer-info">{income.customerName}</span>
                        </td>
                        <td>
                          <span className="payment-method">
                            {hiddenIncomeAuthService.getIncomeTypeDisplay(income.incomeType)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <span style={{ color: income.collectedAmount < 0 ? '#dc2626' : '#059669', fontWeight: 500 }}>
                            {hiddenIncomeAuthService.formatAmount(income.collectedAmount)}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {hiddenIncomeAuthService.formatDate(income.incomeDate)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#6b7280' }}>
                          {hiddenIncomeAuthService.formatDateTime12h(income.createdAt)}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="action-button view"
                              onClick={() => handleViewDetails(income)}
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="action-button approve"
                              onClick={() => handleSingleApprove(income.id)}
                              title="Approve"
                            >
                              <FaCheck />
                            </button>
                            {/* HIDDEN: Individual Reject Button - keeping code for future use */}
                            {/* <button
                              className="action-button reject"
                              onClick={() => handleRejectClick(income)}
                              title="Reject"
                            >
                              <FaTimes />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages} ({total} total)
                  </span>
                  <button
                    className="pagination-button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>

    {/* Approval Date Dialog */}
    {showApproveDialog && (
      <div className="modal-overlay" onClick={() => setShowApproveDialog(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>
              <FaCheckCircle style={{ color: "#10b981", marginRight: "8px" }} />
              {isSingleApprove ? "Approve Receivable Income" : "Approve Receivable Incomes"}
            </h3>
            <button
              className="close-button"
              onClick={() => setShowApproveDialog(false)}
            >
              <FaTimes />
            </button>
          </div>
          <div className="modal-body">
            <p style={{ marginBottom: "16px", color: "#4b5563" }}>
              {isSingleApprove 
                ? "You are about to approve 1 receivable income record."
                : `You are about to approve ${selectedIncomes.length} receivable income record(s).`
              }
            </p>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ 
                display: "block", 
                fontSize: "14px", 
                fontWeight: 500, 
                marginBottom: "8px",
                color: "#374151"
              }}>
                Authorization Date
              </label>
              <input
                type="date"
                value={approvalDate}
                onChange={(e) => setApprovalDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontFamily: "inherit"
                }}
              />
              <p style={{ 
                fontSize: "12px", 
                color: "#6b7280", 
                marginTop: "6px",
                fontStyle: "italic" 
              }}>
                Authorization date can be a previous date if needed
              </p>
            </div>
            <div
              className="modal-actions"
              style={{
                marginTop: "20px",
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="action-button"
                onClick={() => setShowApproveDialog(false)}
                style={{ 
                  backgroundColor: "#6b7280",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "14px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                className="action-button approve"
                onClick={confirmApprove}
                disabled={!approvalDate}
                style={{ 
                  backgroundColor: approvalDate ? "#10b981" : "#9ca3af",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "14px",
                  cursor: approvalDate ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <FaCheck /> Approve {isSingleApprove ? "" : `(${selectedIncomes.length})`}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

      {showRejectDialog && (
        <div
          className="modal-overlay"
          onClick={() => setShowRejectDialog(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Receivable Income</h3>
              <button
                className="close-button"
                onClick={() => setShowRejectDialog(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>Please provide a reason for rejecting this receivable income:</p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                  resize: "vertical",
                  marginTop: "10px",
                }}
              />
              <div
                className="modal-actions"
                style={{
                  marginTop: "20px",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="action-button"
                  onClick={() => setShowRejectDialog(false)}
                  style={{ backgroundColor: "#6b7280" }}
                >
                  Cancel
                </button>
                <button
                  className="action-button reject"
                  onClick={confirmReject}
                  disabled={!rejectionReason.trim()}
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Dialog */}
      {showBulkRejectDialog && (
        <div
          className="modal-overlay"
          onClick={() => setShowBulkRejectDialog(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Reject Receivable Incomes</h3>
              <button
                className="close-button"
                onClick={() => setShowBulkRejectDialog(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>
                You are about to reject {selectedIncomes.length} receivable income
                record(s). Please provide a reason:
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason for all selected records..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                  resize: "vertical",
                  marginTop: "10px",
                }}
              />
              <div
                className="modal-actions"
                style={{
                  marginTop: "20px",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  className="action-button"
                  onClick={() => setShowBulkRejectDialog(false)}
                  style={{ backgroundColor: "#6b7280" }}
                >
                  Cancel
                </button>
                <button
                  className="action-button reject"
                  onClick={confirmBulkReject}
                  disabled={!rejectionReason.trim()}
                >
                  Reject All ({selectedIncomes.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedIncome && (
        <div
          className="modal-overlay"
          onClick={() => setShowDetailsModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Receivable Income Details</h3>
              <button
                className="close-button"
                onClick={() => setShowDetailsModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Customer Information</h4>
                <p>
                  <strong>Name:</strong> {selectedIncome.customerName}
                </p>
                <p>
                  <strong>Booking Ref:</strong> {selectedIncome.bookingRef}
                </p>
                <p>
                  <strong>Unit:</strong> {selectedIncome.unitRef}
                </p>
              </div>

              <div className="detail-section">
                <h4>Income Information</h4>
                <p>
                  <strong>Type:</strong>{" "}
                  {hiddenIncomeAuthService.getIncomeTypeDisplay(
                    selectedIncome.incomeType
                  )}
                </p>
                <p>
                  <strong>Description:</strong> {selectedIncome.description}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {hiddenIncomeAuthService.formatDate(
                    selectedIncome.incomeDate
                  )}
                </p>
              </div>

              <div className="detail-section">
                <h4>Amount Information</h4>
                <p>
                  <strong>Total Amount:</strong>{" "}
                  <span style={{color: selectedIncome.amount < 0 ? '#dc2626' : '#059669'}}>
                    {hiddenIncomeAuthService.formatAmount(selectedIncome.amount)}
                  </span>
                </p>
                <p>
                  <strong>Collected Amount:</strong>{" "}
                  <span style={{color: selectedIncome.collectedAmount < 0 ? '#dc2626' : '#059669'}}>
                    {hiddenIncomeAuthService.formatAmount(
                      selectedIncome.collectedAmount
                    )}
                  </span>
                </p>
                <p>
                  <strong>Pending Amount:</strong>{" "}
                  <span style={{color: selectedIncome.pendingAmount < 0 ? '#dc2626' : '#059669'}}>
                    {hiddenIncomeAuthService.formatAmount(
                      selectedIncome.pendingAmount
                    )}
                  </span>
                </p>
              </div>

              <div className="detail-section">
                <h4>Record Information</h4>
                <p>
                  <strong>Created:</strong>{" "}
                  {hiddenIncomeAuthService.formatDateTime12h(selectedIncome.createdAt)}
                </p>
                <p>
                  <strong>Created By:</strong> {selectedIncome.createdBy}
                </p>
                <p>
                  <strong>Status:</strong> Pending Approval
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HiddenIncomeAuthorize;
